import type { BpeEnvironmentType, BpeConfig, WithConfigs, TenantContext, TenantContextKey, BpeTenantId } from './types';
import { logger } from '../../utils/logger';

const DEFAULT_TENANT_CODE = 'gms';
const DEFAULT_ENVIRONMENT_TYPE: BpeEnvironmentType = 'dev';

export const EMPTY_BPE_CONFIG: BpeConfig = {
  release: 'localhost',
  environmentType: DEFAULT_ENVIRONMENT_TYPE,
  tenant: {
    code: 'utmost',
    id: 'none-dev'
  },
  integrations: {
    mapboxAccessToken: undefined,
    mixpanelAccessToken: undefined,
    launchDarklyClientId: undefined,
    launchDarklyGlobalClientId: undefined,
    sentryDns: undefined,
    googleRecaptchaSiteKey: undefined
  }
};

export class BpeConfigProvider {
  config: BpeConfig;
  private readonly originalTenantContext: TenantContext;
  private readonly originalConfig: TenantContext;
  private readonly originalEnvConfig: TenantContext;
  private readonly isProduction: boolean;
  private readonly tenantContext: boolean;

  private extractEnvironmentType(): BpeEnvironmentType {
    const type = this.extract('TENANT_ID', DEFAULT_ENVIRONMENT_TYPE).split('-').pop()?.toLowerCase();
    return this.isValidEnvironmentType(type) ? type : DEFAULT_ENVIRONMENT_TYPE;
  }

  private isValidEnvironmentType(type: string | undefined): type is BpeEnvironmentType {
    return ['prod', 'sbox', 'impl', 'dev', 'local', 'ci'].includes(type ?? 'unknown');
  }

  private extractTenantCode(): string {
    return this.extract('TENANT_CODE', DEFAULT_TENANT_CODE).toLowerCase();
  }

  private extractTenantId() {
    return (this.extract('TENANT_ID')?.toLowerCase() ??
      `${this.extractTenantCode()}-${this.extractEnvironmentType()}`) as BpeTenantId;
  }

  private extract(tenantContextKey: TenantContextKey): string | undefined;
  private extract(tenantContextKey: TenantContextKey, fallback: string): string;
  private extract(tenantContextKey: TenantContextKey, fallback: string | undefined = undefined): any {
    const key = (tenantContextKey ?? 'UNKNOWN') as keyof typeof this.originalTenantContext;
    if (this.isProduction) {
      return this.originalTenantContext[key] ?? this.originalConfig[key] ?? this.originalEnvConfig[key] ?? fallback;
    }
    return this.originalEnvConfig[key] ?? this.originalTenantContext[key] ?? this.originalConfig[key] ?? fallback;
  }

  constructor(windowObject: WithConfigs, envConfig: TenantContext, isProduction: boolean = false) {
    this.originalTenantContext = 'tenantContext' in windowObject ? { ...windowObject.tenantContext } : {};
    this.originalConfig = 'config' in windowObject ? { ...windowObject.config } : {};
    this.originalEnvConfig = envConfig;
    this.isProduction = isProduction;
    this.tenantContext = !!Object.keys(windowObject.tenantContext ?? {}).length;

    if ('tenantContext' in windowObject || 'config' in windowObject || Object.keys(envConfig).length) {
      this.config = {
        release: windowObject.release || 'localhost',
        environmentType: this.extractEnvironmentType(),
        tenant: {
          code: this.extractTenantCode(),
          id: this.extractTenantId()
        },
        integrations: {
          launchDarklyClientId: this.extract('LD_CLIENT_ID'),
          launchDarklyGlobalClientId: this.extract('LD_GLOBAL_CLIENT_ID'),
          mapboxAccessToken: this.extract('MAPBOX_ACCESS_TOKEN'),
          mixpanelAccessToken: this.extract('MIXPANEL_ACCESS_TOKEN'),
          sentryDns: this.extract('SENTRY_DNS'),
          googleRecaptchaSiteKey: this.extract('GOOGLE_RECAPTCHA_SITE_KEY')
        }
      };
    } else {
      logger.error('window.tenantContext or window.config must be defined');
      this.config = { ...EMPTY_BPE_CONFIG };
    }

    if ('tenantContext' in windowObject) {
      delete windowObject.tenantContext;
    }
    if ('config' in windowObject) {
      delete windowObject.config;
    }
  }

  get release() {
    return this.config.release;
  }

  get environmentType() {
    return this.config.environmentType;
  }

  get hasTenantContext() {
    return this.tenantContext;
  }

  isEnvironmentType(environmentType: BpeEnvironmentType): boolean {
    return this.config.environmentType === environmentType;
  }

  set environmentType(nextType: BpeEnvironmentType | Uppercase<BpeEnvironmentType>) {
    const type = nextType.toLowerCase();
    if (this.isValidEnvironmentType(type)) {
      this.config.environmentType = type;
    } else {
      logger.error(`Invalid environmentType: "${type}"`);
    }
  }

  get tenant() {
    return this.config.tenant;
  }

  get integrations() {
    return this.config.integrations;
  }
}
